import { defineStore } from "pinia";
import { ApiError, api, jsonBody, setCsrfToken } from "../api";

export interface BootstrapData {
  mode: "control" | "mail";
  productName: string;
  domain: string | null;
  accent: string;
  session: {
    email: string;
    loginEmail?: string;
    displayName?: string;
    role: "platform_admin" | "domain_admin" | "member";
    domainId: string | null;
    csrfToken: string;
    expiresAt: string;
  };
}

export interface AuthContextData {
  mode: "control" | "mail";
  productName: string;
  domain: string | null;
  accent: string;
}

interface AuthChallengeData { challengeId: string; expiresIn: number; message: string }

export const useSessionStore = defineStore("session", {
  state: () => ({
    bootstrap: null as BootstrapData | null,
    authContext: null as AuthContextData | null,
    challengeId: "",
    challengeEmail: "",
    authNotice: "",
    authError: "",
    loading: true,
    submitting: false,
    error: "",
  }),
  actions: {
    applyBranding(data: AuthContextData | BootstrapData) {
      document.documentElement.style.setProperty("--accent", data.accent);
      document.title = `${data.productName} · Mail Center`;
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        this.bootstrap = await api<BootstrapData>("/api/v1/bootstrap");
        this.authContext = this.bootstrap;
        this.authError = "";
        setCsrfToken(this.bootstrap.session.csrfToken);
        this.applyBranding(this.bootstrap);
      } catch (error) {
        this.bootstrap = null;
        setCsrfToken("");
        if (error instanceof ApiError && error.status === 401) {
          try {
            this.authContext = await api<AuthContextData>("/api/v1/auth/context");
            this.applyBranding(this.authContext);
          } catch (contextError) {
            this.error = contextError instanceof Error ? contextError.message : "无法读取登录配置";
          }
        } else {
          this.error = error instanceof Error ? error.message : "无法建立安全会话";
        }
      } finally {
        this.loading = false;
      }
    },
    async requestCode(email: string) {
      this.submitting = true;
      this.authError = "";
      this.authNotice = "";
      try {
        const challenge = await api<AuthChallengeData>("/api/v1/auth/request", { method: "POST", ...jsonBody({ email }) });
        this.challengeId = challenge.challengeId;
        this.challengeEmail = email.trim().toLowerCase();
        this.authNotice = challenge.message;
      } catch (error) {
        this.authError = error instanceof Error ? error.message : "验证码发送请求失败";
        throw error;
      } finally {
        this.submitting = false;
      }
    },
    async verifyCode(code: string) {
      this.submitting = true;
      this.authError = "";
      try {
        await api<{ authenticated: boolean }>("/api/v1/auth/verify", {
          method: "POST",
          ...jsonBody({ challengeId: this.challengeId, email: this.challengeEmail, code }),
        });
        this.challengeId = "";
        this.authNotice = "";
        await this.load();
      } catch (error) {
        this.authError = error instanceof Error ? error.message : "验证码校验失败";
        throw error;
      } finally {
        this.submitting = false;
      }
    },
    resetChallenge() {
      this.challengeId = "";
      this.authNotice = "";
      this.authError = "";
    },
    async logout() {
      await api<{ authenticated: boolean }>("/api/v1/auth/logout", { method: "POST" });
      this.bootstrap = null;
      this.challengeId = "";
      setCsrfToken("");
      await this.load();
    },
  },
});
